type SwitchProps = {
  /**
   * On / off — or "mixed" for a control over a group of switches that is
   * partly on. ARIA allows "mixed" on a checkbox, not on a switch, so a mixed
   * control is announced as a (tri-state) checkbox; it looks the same.
   */
  checked: boolean | "mixed";
  onCheckedChange: (next: boolean) => void;
  disabled?: boolean;
  /**
   * While `disabled`: keep it focusable (`aria-disabled`, no native
   * `disabled`) so the reason linked through `ariaDescribedBy` is reachable
   * by keyboard and screen reader. A press does nothing.
   */
  focusableWhenDisabled?: boolean;
  /** "checkbox": a group control that can be "mixed" (see `checked`). */
  role?: "switch" | "checkbox";
  /** Accessible name when no visible label is linked via `ariaLabelledBy`. */
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  testId?: string;
};

/** On/off switch (`role="switch"`), the look of the portal settings toggles. */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  focusableWhenDisabled = false,
  role = "switch",
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  testId,
}: SwitchProps) {
  const softDisabled = disabled && focusableWhenDisabled;
  const on = checked === true;
  const mixed = checked === "mixed";
  return (
    <button
      type="button"
      role={mixed ? "checkbox" : role}
      aria-checked={checked}
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-disabled={softDisabled || undefined}
      disabled={disabled && !softDisabled}
      data-testid={testId}
      onClick={() => {
        if (disabled) return;
        onCheckedChange(!on);
      }}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
        softDisabled ? "opacity-50 cursor-not-allowed" : ""
      } ${on ? "cta-primary" : mixed ? "cta-primary opacity-60" : "surface-quaternary"}`}
    >
      <span
        aria-hidden
        className={`inline-block h-3.5 w-3.5 rounded-full surface-primary shadow-sm transition-transform duration-200 ${
          on ? "translate-x-5" : mixed ? "translate-x-3" : "translate-x-1"
        }`}
      />
    </button>
  );
}
